import Hamburger from "icons/header/Hamburger";
import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import logo from "icons/smallLogo.svg";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { guardedNavigateGlobal } from "hooks/useNavigationGuard";
import DCDialLogo from "icons/DCDialLogo";
import { NotificationIcon } from "icons/header/NotificationIcon";
import { HelpIcon } from "icons/header/HelpIcon";
import Notification from "../header/Notification";
import AiLauncher from "components/ai/AiLauncher";
import { decode, encode } from "utils/HelperFunctions";
import {
  notificationRead,
  updateIsReadAllNotification,
  updateNotificationCount,
  useUserData,
} from "store/slices/authSlice";
import { RootState } from "store/store";
import { useTid } from "utils/testid";

const Navbar = ({ setOpenMenu }: any) => {
  const tid = useTid();
  const { paymentLogo: logo1 } = useAppSelector(
    (state: RootState) => state.payment
  );
  const location = useLocation();
  const navigate = useNavigate();
  const [openNotification, setOpenNotifictaion] = useState<boolean>(false);
  const userData = useUserData();
  const { notificationCount } = useAppSelector(
    (state: RootState) => state.auth
  );

  const { clusterId: cluster_id, id } = useParams();
  const dispatch = useAppDispatch();
  let clusterId = cluster_id
    ? cluster_id
    : userData?.data?.accountData?.clusterid
      ? encode(`${userData?.data?.accountData?.clusterid}`, "ci_")
      : id
        ? encode(decode(id || "")?.split("_")[1], "ci_")
        : "";

  const readAllNotification = async () => {
    try {
      await dispatch(updateIsReadAllNotification(true));
      await dispatch(updateNotificationCount(0));
      let payload = {
        accounts_id: userData?.data?.accountData.id,
        is_all_read: true,
        notificartion_read: true,
        is_read: false,
      };

      await dispatch(notificationRead(payload)).unwrap();
    } catch (error) {
      console.log(" readAllNotification error ======", error);
    }
  };

  return (
    <div
      className="pt-2 pb-3 border-b border-strokePrimary"
      data-testid={tid("mobile-navbar")}
      aria-label="Mobile navigation"
    >
      <div className="flex justify-between items-center w-full">
        <div className="w-full flex justify-start items-center gap-3">
          <span
            className="cursor-pointer"
            onClick={() => setOpenMenu(true)}
            role="button"
            tabIndex={0}
            data-testid={tid("mobile-menu-open-button")}
            aria-label="Open navigation menu"
          >
            <Hamburger aria-hidden="true" />
          </span>
          {location?.pathname?.split("/")?.[1] !== "subscribe" ? (
            /* The brand takes you home, matching the sidebar on desktop. It
               goes through the navigation guard so a half-finished form still
               gets its warning. */
            <span
              className="flex items-center justify-center gap-2 cursor-pointer"
              role="button"
              tabIndex={0}
              aria-label="Go to dashboard"
              data-testid={tid("mobile-brand-link")}
              onClick={() =>
                guardedNavigateGlobal(navigate, `/${clusterId}/dashboard`)
              }
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  guardedNavigateGlobal(navigate, `/${clusterId}/dashboard`);
                }
              }}
            >
              <span className="h-[34px] flex">
                <img
                  src={logo1 ?? logo}
                  alt={logo1 ? "Company logo" : "DCDial logo"}
                  className="object-contain"
                />
              </span>
              {!logo1 && (
                <div className="text-purple font-medium text-xl leading-[30px]">
                  CompanyName
                </div>
              )}
            </span>
          ) : (
            <DCDialLogo aria-hidden="true" />
          )}
        </div>
        <div className="flex gap-3 items-center justify-end w-full">
          {/* Same component as the desktop header; the panel is full-width
              below the sm breakpoint. Renders nothing unless the customer is
              signed in and the AI service accepts their token. */}
          <AiLauncher />
          <div>
            <div
              className="relative"
              role="button"
              tabIndex={0}
              data-testid={tid("mobile-notifications-button")}
              aria-label="Open notifications"
              onClick={() => {
                setOpenNotifictaion((state) => !state);
              }}
            >
              {notificationCount && notificationCount > 0 && (
                <div
                  className={`absolute -top-2 right-0 left-3 ${
                    notificationCount < 10
                      ? "rounded-full h-[19px] w-[19px]"
                      : "rounded-[20px] px-1 py-0.5 w-fit h-[19px]"
                  } border-0.5 z-1 border-white bg-blue grid place-items-center`}
                  data-testid={tid("mobile-notification-count")}
                >
                  <p className="text-center text-[9.14px] leading-[11.19px] font-bold text-white">
                    {notificationCount > 99 ? "99+" : notificationCount}
                  </p>
                </div>
              )}
              <NotificationIcon
                aria-hidden="true"
                strokeColor={
                  openNotification
                    ? "var(--color-blue)"
                    : "var(--color-icon-obscure)"
                }
              />
            </div>
            {openNotification && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  data-testid={tid("mobile-notifications-backdrop")}
                  onClick={() => setOpenNotifictaion(false)}
                />
                <div
                  onClick={(e) => e.stopPropagation()}
                  data-testid={tid("mobile-notifications-panel")}
                  aria-label="Notifications"
                  style={{
                    boxShadow: " 0px 10px 30px 0px #3C42571A",
                    // Grows with the screen rather than sitting at a fixed
                    // height, but never runs past the bottom of the window, so
                    // "See all notifications" always stays reachable.
                    maxHeight: "calc(100dvh - 170px)",
                  }}
                  // left-5/right-5 rather than a width worked out in JS: the
                  // panel now follows the screen, including a rotation while
                  // it is open.
                  className={`left-5 right-5 top-[65px] absolute bg-bgPrimary z-20 rounded-[10px] overflow-hidden flex flex-col`}
                >
                  <Notification setOpenNotifictaion={setOpenNotifictaion} />
                </div>
              </>
            )}
          </div>

          <Link
            to={`/${clusterId}/support-help`}
            className=""
            data-testid={tid("mobile-support-link")}
            aria-label="Support and help"
          >
            <HelpIcon
              aria-hidden="true"
              strokeColor={
                location?.pathname?.split("/")?.[2] === "support-help"
                  ? "var(--color-blue)"
                  : "var(--color-icon-obscure)"
              }
            />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
